import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { currentLocaleTag } from '../../lib/formatting'
import {
  Activity,
  StickyNote,
  Loader2,
  FileText,
  FilePlus,
  UserPlus,
  UserMinus,
  RefreshCw,
  Sparkles,
  Phone,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import {
  type OpportunityEventDto,
  type OpportunityEventType,
} from '@jr2os/shared'

interface Props {
  opportunityId: string
  canWrite: boolean
}

function iconFor(type: OpportunityEventType) {
  switch (type) {
    case 'STAGE_CHANGED':
      return <RefreshCw size={14} className="text-amber-700 dark:text-amber-300" />
    case 'ASSIGNMENT_ADDED':
      return <UserPlus size={14} className="text-emerald-700 dark:text-emerald-300" />
    case 'ASSIGNMENT_REMOVED':
      return <UserMinus size={14} className="text-rose-700 dark:text-rose-300" />
    case 'DOCUMENT_ADDED':
      return <FilePlus size={14} className="text-blue-700 dark:text-blue-300" />
    case 'DOCUMENT_VERSION_ADDED':
      return <FileText size={14} className="text-blue-700 dark:text-blue-300" />
    case 'NOTE':
      return <StickyNote size={14} className="text-fuchsia-700 dark:text-fuchsia-300" />
    case 'CONTACT_LOG':
      return <Phone size={14} className="text-sky-700 dark:text-sky-300" />
    case 'QUOTE_SENT':
      return <FileText size={14} className="text-blue-700 dark:text-blue-300" />
    case 'QUOTE_ACCEPTED':
      return <Sparkles size={14} className="text-emerald-700 dark:text-emerald-300" />
  }
}

export default function TimelinePanel({ opportunityId, canWrite }: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [events, setEvents] = useState<OpportunityEventDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const reload = useCallback(async () => {
    try {
      const { items } = await api.get<{ items: OpportunityEventDto[] }>(
        `/opportunities/${opportunityId}/events`
      )
      setEvents(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('timeline.errors.loadEvents'))
    }
  }, [opportunityId, t])

  useEffect(() => {
    void reload()
  }, [reload])

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Activity size={16} className="text-jr2-700 dark:text-jr2-300" />
          {t('timeline.title')}
          {events && <span className="text-xs t-muted font-normal">({events.length})</span>}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/15 hover:bg-jr2-500/25 flex items-center gap-1"
          >
            <StickyNote size={14} /> {showForm ? t('common:actions.cancel') : t('timeline.addNote')}
          </button>
        )}
      </div>

      {showForm && (
        <NoteForm
          opportunityId={opportunityId}
          onCreated={() => {
            setShowForm(false)
            void reload()
          }}
        />
      )}

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 mb-3">
          {error}
        </div>
      )}

      {!events ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('common:status.loading')}
        </div>
      ) : events.length === 0 ? (
        <p className="t-muted text-sm">{t('timeline.empty')}</p>
      ) : (
        <ol className="relative space-y-3 pl-9 mt-2">
          <span
            className="absolute left-3.5 top-1 bottom-1 w-px"
            style={{ background: 'var(--border)' }}
          />
          {events.map((e) => (
            <li key={e.id} className="relative">
              <span
                className="absolute -left-[30px] top-1 w-5 h-5 rounded-full grid place-items-center"
                style={{
                  background: 'var(--bg-surface-strong)',
                  border: '1px solid var(--border)',
                }}
              >
                {iconFor(e.type)}
              </span>
              <div className="text-sm t-primary leading-snug">{e.title}</div>
              {e.description && <div className="text-xs t-secondary mt-1">{e.description}</div>}
              <div className="text-xs t-faint mt-1">
                {new Date(e.occurredAt).toLocaleString(currentLocaleTag())}
                {e.createdByName && ` · ${e.createdByName}`}
                <span className="ml-2 text-[10px] uppercase tracking-wider text-jr2-700 dark:text-jr2-300/80">
                  {t('enums:OpportunityEventType.' + e.type)}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

function NoteForm({
  opportunityId,
  onCreated,
}: {
  opportunityId: string
  onCreated: () => void
}) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<'NOTE' | 'CONTACT_LOG'>('NOTE')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.post(`/opportunities/${opportunityId}/events`, {
        title,
        description: description || null,
        type,
      })
      setTitle('')
      setDescription('')
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('common:errors.network'))
      }
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <form onSubmit={onSubmit} className="space-y-2 surface-soft p-3 mb-3">
      <div className="grid grid-cols-3 gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
          className={`${inputCls} col-span-1`}
        >
          <option value="NOTE">{t('timeline.noteForm.typeNote')}</option>
          <option value="CONTACT_LOG">{t('timeline.noteForm.typeContactLog')}</option>
        </select>
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('timeline.noteForm.titlePlaceholder')}
          className={`${inputCls} col-span-2`}
        />
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder={t('timeline.noteForm.descriptionPlaceholder')}
        className={inputCls}
      />
      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="text-right">
        <button
          type="submit"
          disabled={busy || !title}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 inline-flex items-center gap-1"
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          {t('common:actions.add')}
        </button>
      </div>
    </form>
  )
}
